import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum } from "@/lib/format";
import { cn } from "@/lib/utils";
import { 
  ArrowLeft, LineChart as ChartIcon, Zap, Target, TrendingUp, 
  CalendarDays, BarChart3, Info, Building2, Users, ShoppingCart, 
  MapPin, Layers, AlertTriangle, ArrowRightLeft, CreditCard, Box, Search
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { 
  BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ComposedChart, Cell, PieChart, Pie
} from "recharts";

const COLORS = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", 
  "#EC4899", "#14B8A6", "#F97316", "#06B6D4", "#6366F1"
];

const DIAS_SEMANA = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];



export default function AnalyticsPage() {
  const { dashboard, filteredNotas, privacyMode } = useFiscalStore();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  const [q, setQ] = useState("");
  const [searchFornecedores, setSearchFornecedores] = useState("");
  const [searchClientes, setSearchClientes] = useState("");
  
  useEffect(() => { document.title = "Data Science & BI — DataGrid XML"; }, []);

  const intelligence = dashboard?.intelligence;

  const { 
    topFornecedores, topClientes, topProdutos,
    topPagamentos, topAssociacoes, outliersPreco, clientesRisco,
    relacaoCompraVenda, balancoGeral, comprasPorUF
  } = useMemo(() => {
    if (!intelligence) return {
      topFornecedores: [], topClientes: [], topProdutos: [],
      topPagamentos: [], topAssociacoes: [], outliersPreco: [], clientesRisco: [],
      relacaoCompraVenda: [], balancoGeral: { totalCompras: 0, totalVendas: 0, resultado: 0 },
      comprasPorUF: []
    };
    return intelligence;
  }, [intelligence]);

  const { 
    curvaABC, 
    resumoABC,
    inflacaoTopProdutos,
    sazonalidadeSemana,
    metricasRFM
  } = useMemo(() => {
    const produtosMap = new Map<string, { nome: string; valorTotal: number; qtd: number }>();
    const precosTempoMap = new Map<string, Map<string, { somaPreco: number; count: number }>>();
    const volumePorDia = [0, 0, 0, 0, 0, 0, 0];
    const rfmMap = new Map<string, { nome: string; recencia: number; frequencia: number; monetario: number; ultimaData: Date }>();
    const dataAtual = new Date();

    let totalVolumeProdutos = 0;

    for (const nota of filteredNotas) {
      if (nota.notaStatus === "CANCELADA" || nota.notaStatus === "INUTILIZADA") continue;
      const isEntrada = nota.tipoOperacao === "ENTRADA";
      const mesAno = nota.emissao ? `${new Date(nota.emissao).getFullYear()}-${String(new Date(nota.emissao).getMonth() + 1).padStart(2, '0')}` : "ND";

      if (nota.emissao) {
        const d = new Date(nota.emissao);
        if (!isNaN(d.getTime())) volumePorDia[d.getDay()] += nota.valorOficial;
      }

      if (isEntrada && nota.emitente && nota.emissao) {
        const cnpj = nota.cnpjEmit || "ND";
        const d = new Date(nota.emissao);
        if (!rfmMap.has(cnpj)) {
          rfmMap.set(cnpj, { nome: nota.emitente, recencia: 0, frequencia: 0, monetario: 0, ultimaData: new Date(0) });
        }
        const rfm = rfmMap.get(cnpj)!;
        rfm.frequencia += 1;
        rfm.monetario += nota.valorOficial;
        if (d > rfm.ultimaData) rfm.ultimaData = d;
      }

      if (nota.itens) {
        for (const item of nota.itens) {
          const nomeProd = item.xProd || "Desconhecido";
          if (!produtosMap.has(nomeProd)) produtosMap.set(nomeProd, { nome: nomeProd, valorTotal: 0, qtd: 0 });
          produtosMap.get(nomeProd)!.valorTotal += item.vProd;
          produtosMap.get(nomeProd)!.qtd += item.qCom;
          totalVolumeProdutos += item.vProd;

          if (isEntrada && mesAno !== "ND") {
             if (!precosTempoMap.has(nomeProd)) precosTempoMap.set(nomeProd, new Map());
             const tempos = precosTempoMap.get(nomeProd)!;
             if (!tempos.has(mesAno)) tempos.set(mesAno, { somaPreco: 0, count: 0 });
             tempos.get(mesAno)!.somaPreco += (item.vUnCom || 0);
             tempos.get(mesAno)!.count += 1;
          }
        }
      }
    }

    const produtosOrdenados = Array.from(produtosMap.values()).sort((a, b) => b.valorTotal - a.valorTotal);
    let acumulado = 0;
    let classeA = 0, classeB = 0, classeC = 0;
    
    const curvaABCData = produtosOrdenados.map((p) => {
      acumulado += p.valorTotal;
      const percAcumulado = (acumulado / totalVolumeProdutos) * 100;
      let classe = "C";
      if (percAcumulado <= 80) { classe = "A"; classeA++; }
      else if (percAcumulado <= 95) { classe = "B"; classeB++; }
      else { classeC++; }

      return {
        nome: p.nome.length > 20 ? p.nome.substring(0,20)+"..." : p.nome,
        nomeCompleto: p.nome,
        valor: p.valorTotal,
        percAcumulado,
        classe
      };
    }).slice(0, 30);

    const sazonalidadeSemanaData = DIAS_SEMANA.map((dia, index) => ({ dia, volume: volumePorDia[index] }));

    const top3Custos = produtosOrdenados.filter(p => precosTempoMap.has(p.nome) && precosTempoMap.get(p.nome)!.size > 1).slice(0, 3);
    const mesesSet = new Set<string>();
    top3Custos.forEach(p => Array.from(precosTempoMap.get(p.nome)!.keys()).forEach(m => mesesSet.add(m)));
    const mesesOrdenados = Array.from(mesesSet).sort();

    const inflacaoData = mesesOrdenados.map(mes => {
      const dataPoint: any = { mes };
      top3Custos.forEach((p, idx) => {
        const hist = precosTempoMap.get(p.nome)!.get(mes);
        dataPoint[`Prod${idx+1}`] = hist ? hist.somaPreco / hist.count : null;
      });
      return dataPoint;
    });

    const rfmList = Array.from(rfmMap.values()).map(r => {
      const recenciaDias = Math.floor((dataAtual.getTime() - r.ultimaData.getTime()) / (1000 * 3600 * 24));
      return { ...r, recenciaDias };
    });

    let vip = 0, regular = 0, risco = 0;
    rfmList.forEach(r => {
      if (r.frequencia >= 3 && r.recenciaDias <= 60) vip++;
      else if (r.recenciaDias > 120) risco++;
      else regular++;
    });

    return {
      curvaABC: curvaABCData,
      resumoABC: { classeA, classeB, classeC, total: produtosOrdenados.length },
      sazonalidadeSemana: sazonalidadeSemanaData,
      inflacaoTopProdutos: { dados: inflacaoData, series: top3Custos.map(p => p.nome) },
      metricasRFM: { vip, regular, risco, total: rfmList.length }
    };
  }, [filteredNotas]);

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-6 animate-in fade-in duration-1000">
      
      {/* Hub de Evolução Histórica (Pulse) */}
      <div className="glass-card p-8 border-primary/5 bg-white/[0.01]">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 mb-10">
          <div>
            <h2 className="text-2xl font-black tracking-tighter flex items-center gap-3">
              <TrendingUp className="size-6 text-primary" /> Hub de Evolução Histórica
            </h2>
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Análise Comparativa de Safras e Ciclos Operacionais</p>
          </div>
          <div className="flex items-center gap-4">
             <div className="flex flex-col items-end">
                <span className="text-[9px] font-black uppercase text-muted-foreground">Crescimento de Volume</span>
                <span className={cn(
                  "text-xl font-black tabular-nums",
                  (dashboard?.stats?.volumeTrend || 0) >= 0 ? "text-emerald-500" : "text-red-500"
                )}>
                  {(dashboard?.stats?.volumeTrend || 0) >= 0 ? "+" : ""}{dashboard?.stats?.volumeTrend?.toFixed(1)}%
                </span>
             </div>
             <div className="h-10 w-px bg-foreground/10" />
             <div className="flex flex-col items-end">
                <span className="text-[9px] font-black uppercase text-muted-foreground">Projeção Próximo Ciclo</span>
                <span className="text-xl font-black text-primary tabular-nums">
                  {fmtBRL((balancoGeral?.totalVendas || 0) * (1 + (dashboard?.stats?.revenueTrend || 0) / 100))}
                </span>
             </div>
          </div>
        </div>

        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={useMemo(() => {
              const months: Record<string, any> = {};
              filteredNotas.forEach(n => {
                if (!n.emissao) return;
                const date = new Date(n.emissao);
                if (isNaN(date.getTime())) return;
                const k = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                if (!months[k]) months[k] = { mes: k, Revenue: 0, Taxes: 0, Volume: 0 };
                months[k].Revenue += n.valorOficial || 0;
                months[k].Taxes += (n.vICMS || 0) + (n.vPIS || 0) + (n.vCOFINS || 0);
                months[k].Volume += 1;
              });
              return Object.values(months).sort((a, b) => a.mes.localeCompare(b.mes)).slice(-6);
            }, [filteredNotas])}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-5" />
              <XAxis dataKey="mes" tick={{ fontSize: 10, fontWeight: 900 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fontWeight: 900 }} axisLine={false} tickLine={false} tickFormatter={v => `R$${v/1000}k`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fontWeight: 900 }} axisLine={false} tickLine={false} />
              <RechartsTooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.95)', border: 'none', borderRadius: '16px', color: 'white' }} />
              <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '30px', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase' }} />
              <Area yAxisId="left" type="monotone" dataKey="Revenue" stroke="#3B82F6" strokeWidth={4} fillOpacity={1} fill="url(#colorRevenue)" />
              <Line yAxisId="left" type="monotone" dataKey="Taxes" stroke="#EF4444" strokeWidth={3} dot={{ r: 4 }} />
              <Bar yAxisId="right" dataKey="Volume" fill="#10B981" radius={[4, 4, 0, 0]} barSize={12} opacity={0.3} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* PAINEL 1: Curva ABC */}
      <div className="glass-card p-8 border-primary/5 bg-white/[0.01] relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
          <Target className="size-32 rotate-12" />
        </div>
        <div className="flex flex-col lg:flex-row justify-between items-start gap-8">
          <div className="flex-1">
            <h2 className="text-2xl font-black uppercase tracking-tighter mb-2">Curva ABC (Pareto)</h2>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-8">Identificação dos 20% de produtos que geram 80% do volume financeiro</p>
            
            <div className="grid grid-cols-3 gap-4 mb-8">
              <SummaryBox label="Classe A (80%)" value={resumoABC.classeA} color="text-emerald-500" />
              <SummaryBox label="Classe B (15%)" value={resumoABC.classeB} color="text-primary" />
              <SummaryBox label="Classe C (5%)" value={resumoABC.classeC} color="text-slate-400" />
            </div>
          </div>
          <div className="w-full lg:w-2/3 h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={curvaABC}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-5" />
                <XAxis dataKey="nome" tick={{ fontSize: 9, fontWeight: 900 }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fontWeight: 900 }} tickFormatter={(val) => `R$${(val/1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fontWeight: 900 }} tickFormatter={(val) => `${val}%`} domain={[0, 100]} axisLine={false} tickLine={false} />
                <RechartsTooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: 'none', borderRadius: '16px', color: 'white' }} />
                <Bar yAxisId="left" dataKey="valor" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={20}>
                  {curvaABC.map((entry, index) => <Cell key={index} fill={entry.classe === "A" ? "#10B981" : entry.classe === "B" ? "#3B82F6" : "#94A3B8"} />)}
                </Bar>
                <Line yAxisId="right" type="monotone" dataKey="percAcumulado" stroke="#F59E0B" strokeWidth={3} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Balanço de Operações */}
        <div className="glass-card p-8 border-primary/5 hover:border-primary/20 transition-all">
          <h2 className="text-xl font-black uppercase tracking-widest mb-8 flex items-center gap-3">
            <ArrowRightLeft className="size-5 text-primary" /> Balanço de Operações
          </h2>
          <div className="flex flex-col xl:flex-row gap-8 items-center">
            <div className="xl:w-1/2 space-y-4">
              <SimpleStat label="Receita Total" value={fmtBRL(balancoGeral.totalVendas)} color="text-emerald-500" />
              <SimpleStat label="Custos Totais" value={fmtBRL(balancoGeral.totalCompras)} color="text-amber-500" />
              <div className="pt-4 border-t border-foreground/5">
                <SimpleStat label="Margem Líquida" value={fmtBRL(balancoGeral.resultado)} color={balancoGeral.resultado >= 0 ? "text-primary" : "text-red-500"} />
              </div>
            </div>
            <div className="xl:w-1/2 h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={relacaoCompraVenda} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 900 }} width={60} />
                  <RechartsTooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: 'none', borderRadius: '16px' }} />
                  <Bar dataKey="Compra" fill="#FF9A3D" radius={[0, 4, 4, 0]} barSize={20} />
                  <Bar dataKey="Venda" fill="#10B981" radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Matriz RFM */}
        <div className="glass-card p-8 border-primary/5 hover:border-primary/20 transition-all">
          <h2 className="text-xl font-black uppercase tracking-widest mb-8 flex items-center gap-3">
            <BarChart3 className="size-5 text-emerald-500" /> Matriz RFM (Fornecedores)
          </h2>
          <div className="grid grid-cols-1 gap-4">
            <RFMBox label="Fornecedores VIP" sub="Frequentes e Ativos" value={metricasRFM.vip} color="bg-emerald-500/10 text-emerald-500" border="border-emerald-500/20" />
            <RFMBox label="Regulares" sub="Atividade Moderada" value={metricasRFM.regular} color="bg-blue-500/10 text-blue-500" border="border-blue-500/20" />
            <RFMBox label="Em Risco" sub="Inativos +120 dias" value={metricasRFM.risco} color="bg-red-500/10 text-red-500" border="border-red-500/20" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Market Basket */}
        <div className="glass-card p-8 border-primary/5 bg-primary/5">
          <h2 className="text-lg font-black uppercase tracking-widest mb-6 flex items-center gap-3 text-primary">
            <Layers className="size-4" /> Cesta de Compras
          </h2>
          <div className="space-y-4">
            {topAssociacoes.length === 0 ? (
              <EmptyState icon={Layers} message="Dados insuficientes" />
            ) : topAssociacoes.map((assoc: any, i: number) => (
              <div key={i} className="p-4 rounded-2xl glass-technical border-foreground/5 hover:border-primary/30 transition-all">
                <div className="flex justify-between items-center text-[10px] font-black text-primary mb-2">
                  <span>COMBO DETECTADO</span>
                  <span>{assoc.count}X</span>
                </div>
                <div className="text-xs font-bold truncate flex items-center gap-2">
                   <span className="truncate">{assoc.p1}</span>
                   <ArrowRightLeft className="size-3 opacity-30" />
                   <span className="truncate">{assoc.p2}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Churn Risk */}
        <div className="glass-card p-8 border-primary/5 bg-red-500/5">
          <h2 className="text-lg font-black uppercase tracking-widest mb-6 flex items-center gap-3 text-red-500">
            <AlertTriangle className="size-4" /> Risco de Inatividade
          </h2>
          <div className="space-y-4">
            {clientesRisco.length === 0 ? (
              <EmptyState icon={AlertTriangle} message="Nenhum risco detectado" />
            ) : clientesRisco.map((cliente: any, i: number) => (
              <div key={i} className="p-4 rounded-2xl glass-technical border-foreground/5 hover:border-red-500/30 transition-all">
                <div className="flex justify-between items-center mb-2">
                   <span className={cn("text-[9px] font-black px-2 py-0.5 rounded", cliente.status === "CRÍTICO" ? "bg-red-500/10 text-red-500" : "bg-amber-500/10 text-amber-500")}>{cliente.status}</span>
                   <span className="text-[9px] font-black opacity-50">{cliente.inatividade} DIAS INATIVO</span>
                </div>
                <div className="text-xs font-bold truncate">{cliente.nome}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Price Outliers */}
        <div className="glass-card p-8 border-primary/5 bg-emerald-500/5">
          <h2 className="text-lg font-black uppercase tracking-widest mb-6 flex items-center gap-3 text-emerald-500">
            <Zap className="size-4" /> Anomalias de Preço
          </h2>
          <div className="space-y-4">
            {outliersPreco.length === 0 ? (
              <EmptyState icon={Zap} message="Nenhuma anomalia" />
            ) : outliersPreco.map((outlier: any, i: number) => (
              <div key={i} className="p-4 rounded-2xl glass-technical border-foreground/5 hover:border-emerald-500/30 transition-all">
                <div className="flex justify-between items-center text-[10px] font-black text-emerald-500 mb-2">
                   <span>Z-SCORE: {outlier.desvio}</span>
                   <span className="opacity-50">AVG: {fmtBRL(outlier.media)}</span>
                </div>
                <div className="text-xs font-bold truncate">{outlier.produto}</div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-emerald-500/10">
                   <span className="text-[9px] font-black opacity-60">PREÇO IDENTIFICADO</span>
                   <span className="text-xs font-black">{fmtBRL(outlier.preco)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sazonalidade & Inflação */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card p-8 border-primary/5">
           <h2 className="text-xl font-black uppercase tracking-widest mb-8 flex items-center gap-3">
             <CalendarDays className="size-5 text-orange-500" /> Matriz de Demanda (Sazonalidade)
           </h2>
           <div className="h-[250px]">
             <ResponsiveContainer width="100%" height="100%">
               <BarChart data={sazonalidadeSemana}>
                 <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-5" />
                 <XAxis dataKey="dia" tick={{ fontSize: 9, fontWeight: 900 }} axisLine={false} tickLine={false} />
                 <RechartsTooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: 'none', borderRadius: '16px' }} />
                 <Bar dataKey="volume" fill="#F97316" radius={[6, 6, 0, 0]} barSize={32} />
               </BarChart>
             </ResponsiveContainer>
           </div>
        </div>

        <div className="glass-card p-8 border-primary/5">
           <h2 className="text-xl font-black uppercase tracking-widest mb-8 flex items-center gap-3">
             <TrendingUp className="size-5 text-red-500" /> Rastreador de Inflação
           </h2>
           <div className="h-[250px]">
             <ResponsiveContainer width="100%" height="100%">
               <LineChart data={inflacaoTopProdutos.dados}>
                 <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-5" />
                 <XAxis dataKey="mes" tick={{ fontSize: 9, fontWeight: 900 }} axisLine={false} tickLine={false} />
                 <YAxis tick={{ fontSize: 9, fontWeight: 900 }} tickFormatter={v => `R$${v}`} axisLine={false} tickLine={false} />
                 <RechartsTooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: 'none', borderRadius: '16px' }} />
                 <Legend iconType="circle" wrapperStyle={{ fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }} />
                 {inflacaoTopProdutos.series.map((prod: string, idx: number) => (
                   <Line key={idx} type="monotone" dataKey={`Prod${idx+1}`} name={prod.substring(0, 15)} stroke={COLORS[idx]} strokeWidth={3} dot={{ r: 4 }} connectNulls />
                 ))}
               </LineChart>
             </ResponsiveContainer>
           </div>
        </div>
      </div>
    </div>
  );
}

function SummaryBox({ label, value, color }: any) {
  return (
    <div className="flex flex-col text-center p-4 rounded-2xl glass-technical border-foreground/5 bg-white/5">
      <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1">{label}</span>
      <span className={cn("text-2xl font-black tabular-nums tracking-tighter", color)}>{fmtNum(value)}</span>
    </div>
  );
}

function SimpleStat({ label, value, color }: any) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className={cn("text-lg font-black tabular-nums tracking-tighter", color)}>{value}</span>
    </div>
  );
}

function RFMBox({ label, sub, value, color, border }: any) {
  return (
    <div className={cn("flex items-center justify-between p-6 rounded-2xl border transition-all hover:scale-[1.02]", color, border)}>
      <div>
        <div className="font-black text-sm uppercase tracking-wider">{label}</div>
        <div className="text-[10px] opacity-70 uppercase font-black">{sub}</div>
      </div>
      <div className="text-4xl font-black tabular-nums">{value}</div>
    </div>
  );
}

function EmptyState({ icon: Icon, message }: any) {
  return (
    <div className="p-8 text-center opacity-20 flex flex-col items-center justify-center gap-2">
      <Icon className="size-12" />
      <span className="text-[10px] font-black uppercase tracking-widest">{message}</span>
    </div>
  );
}
